import { must } from "@/common/utils/must";
import { eventNames, valueNames } from "@/modules/Constants";
import { clusterTypes, integrationTypes } from "@/modules/ExperienceData";
import { isAskEnabled } from "@/modules/askEnabled";
import { isFeatureOn } from "@/modules/features";
import {
  checkAssistantAvailability,
  isAssistantAvailable,
} from "@/modules/assistantAvailability";
import { buildPublisherAssistantAvailabilityApiUrl } from "@/modules/experienceApiUrl";
import config from "@/modules/config";
import LinkData, { linkTypes } from "@/plugin/LinkData";
import ShopWindowConfig, { openingSlideType } from "@/plugin/ShopWindowConfig";
import ShopWindowConfigList from "@/plugin/ShopWindowConfigList";
import * as labelUtils from "@/plugin/labelUtils";
import {
  clusterNames,
  SHOP_WINDOW_TEMPLATES,
  shopWindowNames,
  tabAliases,
} from "@/plugin/recipeShopWindows";
import {
  registerMyxxData,
  registerWhiskData,
} from "@/plugin/recipeIntegrations";
import { resolveProductSlide } from "@/plugin/recipeDeepLinks";
import { revealAsk } from "@/plugin/recipeAsk";
import type { ExperienceState, PluginEvent } from "@/types/state";

export { clusterNames, shopWindowNames } from "@/plugin/recipeShopWindows";

export default class RecipePlugin {
  static SHOP_WINDOW_TEMPLATES = SHOP_WINDOW_TEMPLATES;

  declare shopWindowMap: Record<string, ShopWindowConfig>;

  init(state: ExperienceState) {
    const applicationData = state.applicationData;
    const experienceData = state.experienceData;
    const integrationType = experienceData.getIntegrationType();

    this.shopWindowMap = {};

    for (
      let n = 0, max = RecipePlugin.SHOP_WINDOW_TEMPLATES.length;
      n < max;
      n++
    ) {
      const template = RecipePlugin.SHOP_WINDOW_TEMPLATES[n];
      const config = ShopWindowConfig.fromTemplate(template);
      this.shopWindowMap[config.name] = config.init(experienceData);
    }

    if (integrationType === integrationTypes.WHISK) {
      registerWhiskData(this.shopWindowMap, experienceData);
    } else if (integrationType === integrationTypes.MYXX) {
      registerMyxxData(this.shopWindowMap, experienceData);
    } else if (integrationType == null) {
      if (experienceData.url) {
        this.shopWindowMap[shopWindowNames.LIST].links = {
          cta: new LinkData(
            linkTypes.URL,
            experienceData.url,
            "ingredients.cta",
            true,
            labelUtils.getClusterLabel(
              clusterNames.INGREDIENT,
              experienceData.style,
            ),
          ),
        };
      }
    }

    let oSlideType;

    if (experienceData.hasRecipeMetadata()) {
      oSlideType = openingSlideType.CUSTOM;
    } else if (experienceData.styleFlags.showPanelHintsView()) {
      oSlideType = openingSlideType.HINTS;
    } else {
      oSlideType = openingSlideType.NONE;
    }

    const showHintTap = experienceData.styleFlags.showPanelHintTap();

    const cookware = this.shopWindowMap[shopWindowNames.COOKWARE];
    cookware.data.showHintTap = showHintTap;
    cookware.links = {
      cta: new LinkData(
        linkTypes.ARTICLE,
        undefined,
        "cookware.cta",
        true,
        labelUtils.getClusterLabel(clusterNames.COOKWARE, experienceData.style),
      ),
    };

    const wine = this.shopWindowMap[shopWindowNames.WINE];
    wine.data.showHintTap = showHintTap;
    wine.links = {
      cta: new LinkData(
        linkTypes.ARTICLE,
        undefined,
        "wine.cta",
        true,
        labelUtils.getClusterLabel(clusterNames.WINE, experienceData.style),
      ),
    };

    const book = this.shopWindowMap[shopWindowNames.BOOK];
    book.data.showHintTap = showHintTap;
    book.links = {
      cta: new LinkData(
        linkTypes.ARTICLE,
        undefined,
        "book.cta",
        true,
        labelUtils.getClusterLabel(clusterNames.BOOK, experienceData.style),
      ),
    };

    const product = this.shopWindowMap[shopWindowNames.PRODUCT];
    product.data.showHintTap = showHintTap;
    product.data.openingSlideType = oSlideType;
    if (product.links === undefined) {
      const links = {
        cta: new LinkData(
          linkTypes.ARTICLE,
          undefined,
          "product.cta",
          true,
          labelUtils.getClusterLabel(
            clusterNames.PRODUCT,
            experienceData.style,
          ),
        ),
      };

      product.links = links;
    }

    const method = this.shopWindowMap[shopWindowNames.METHOD];
    method.data.openingSlideType = oSlideType;

    applicationData.setValue(valueNames.HAS_CONTROL_BAR, true);

    this._registerShopWindows(state);
    this._maybeCheckAssistantAvailability(state);
  }

  // When the publisher has opted in but the load-time
  // availability check hasn't run yet, fire it once. On confirmation the Pixie
  // tab is revealed; a capped or unreachable assistant leaves it hidden. Once
  // confirmed available, `_registerShopWindows` includes the tab directly on
  // every later build (e.g. chain advance), so this is a no-op then.
  _maybeCheckAssistantAvailability(state: ExperienceState) {
    const experienceData = state.experienceData;
    if (!isAskEnabled(experienceData) || isAssistantAvailable()) {
      return;
    }
    const url = buildPublisherAssistantAvailabilityApiUrl({
      experienceId: experienceData.idexperience,
    });
    const self = this;
    checkAssistantAvailability(url, function () {
      revealAsk(self.shopWindowMap, state);
    });
  }

  _registerShopWindows(state: ExperienceState) {
    const experienceData = state.experienceData;
    const applicationData = state.applicationData;
    const pluginWindows = RecipePlugin.SHOP_WINDOW_TEMPLATES;
    let n, max, m, maxm, i;
    let pluginWindow;
    let shopWindowConfig: ShopWindowConfig | undefined;
    let shopWindowClusterNames: string[];
    let add;
    let clusterType;

    const shopWindowConfigs = new ShopWindowConfigList();
    let defaultWindow: ShopWindowConfig | null = null;
    let firstWindow: ShopWindowConfig | null = null;
    let videoTimelineName: string | null = null;
    const used: Record<string, boolean> = {};

    if (config.getVisibleMethodSteps() === 0) {
      used[clusterNames.METHOD] = true;
    }

    const hasEssentials = must(experienceData.articles, "articles").find(
      article => article.type === "article",
    );

    // First pass: collect all valid shop windows
    const validShopWindows: {
      shopWindowConfig: ShopWindowConfig;
      pluginWindow: ShopWindowConfig;
      clusterType: string;
      clusterNames: string[];
    }[] = [];
    for (n = 0, max = pluginWindows.length; n < max; n++) {
      pluginWindow = pluginWindows[n];
      shopWindowConfig = this.shopWindowMap[pluginWindow.name];

      // Shop windows without icons do not show up in the list of configs.
      if (
        !shopWindowConfig.icon ||
        (pluginWindow.name === clusterNames.PRODUCT && !hasEssentials)
      ) {
        continue;
      }

      shopWindowClusterNames = shopWindowConfig.clusterNames();
      add = true;

      // Ask has no CDP cluster behind it, so it stands in as its own.
      // Hide Pixie unless this publisher opted in (default-off).
      // Also hold it out until the load-time availability check
      // confirms the assistant is within its global spend ceiling — so a
      // capped assistant shows no tab at all. `isAssistantAvailable()` is
      // false until confirmed; `_maybeCheckAssistantAvailability` reveals the
      // tab once the check returns available.
      const isAsk = pluginWindow.name === shopWindowNames.ASK;
      if (isAsk && !(isAskEnabled(experienceData) && isAssistantAvailable())) {
        continue;
      }
      const isTest = pluginWindow.name === shopWindowNames.TEST;
      if (isTest && !isFeatureOn(experienceData, "demo.test_tab")) {
        continue;
      }
      clusterType =
        isAsk || isTest
          ? clusterTypes.EXPERIENCE
          : experienceData.getClusterType(shopWindowConfig.clusterName);
      if (clusterType !== clusterTypes.NONE) {
        for (m = 0, maxm = shopWindowClusterNames.length; m < maxm; m++) {
          if (shopWindowClusterNames[m] in used) {
            add = false;
          }
        }

        if (add) {
          validShopWindows.push({
            shopWindowConfig,
            pluginWindow,
            clusterType,
            clusterNames: shopWindowClusterNames,
          });

          // Mark clusters as used
          for (m = 0, maxm = shopWindowClusterNames.length; m < maxm; m++) {
            used[shopWindowClusterNames[m]] = true;
          }
        }
      }
    }

    // Add all valid shop windows to the config list
    for (i = 0; i < validShopWindows.length; i++) {
      const {
        shopWindowConfig: config,
        pluginWindow: pw,
        clusterType: ct,
      } = validShopWindows[i];
      shopWindowConfigs.add(config);

      if (firstWindow === null && pw.isPortraitOverlay === false) {
        firstWindow = config;
      }

      // The video timeline (yellow markers + auto-swipe) and the default
      // window follow the FIRST timeline cluster in window order — Method for
      // recipes. The product/Essentials window is still added as a tab above
      // regardless; selecting any tab re-points VIDEO_TIMELINE_NAME to that
      // tab's cluster via RootPlugin (on SHOP_WINDOW_CONFIG change), so the
      // markers and swipe follow whichever tab is active.
      if (ct === clusterTypes.TIMELINE) {
        if (videoTimelineName === null && pw.isPortraitOverlay === false) {
          defaultWindow = config;
          videoTimelineName = config.clusterName;
        }
      }
    }

    applicationData.setValue(
      valueNames.SHOP_WINDOW_CONFIG_LIST,
      shopWindowConfigs,
    );

    const defaultWindowName = config.defaultShopWindow();
    if (defaultWindowName) {
      shopWindowConfig = shopWindowConfigs.atName(defaultWindowName);
      if (shopWindowConfig) {
        defaultWindow = shopWindowConfig;
        if (
          experienceData.getClusterType(shopWindowConfig.clusterName) ===
          clusterTypes.TIMELINE
        ) {
          videoTimelineName = shopWindowConfig.clusterName;
        }
      }
    }

    // Play-page deep-link: ?tab=<token> opens a named shop window on load.
    // Friendly tokens (products, equipment, ingredients, essentials) map to
    // internal names; unknown tokens fall through verbatim so exact names work
    // too. Overrides the dsw default; silently no-ops when the window is absent.
    const openTab = config.getOpenTab();
    if (openTab) {
      const tabWindow = shopWindowConfigs.atName(
        tabAliases[openTab] || openTab,
      );
      if (tabWindow) {
        defaultWindow = tabWindow;
        if (
          experienceData.getClusterType(tabWindow.clusterName) ===
          clusterTypes.TIMELINE
        ) {
          videoTimelineName = tabWindow.clusterName;
        }
      }
    }

    // Play-page deep-link: ?step=<n> (1-based) opens the Method window on that
    // step's slide; when Method is a timeline cluster the video timeline couples
    // so playback seeks to the step. Overrides ?tab; silently no-ops when the
    // Method window is absent or n is out of range. Slide indexing mirrors
    // resolveProductSlide (opening-slide offset + 0-based cluster index).
    const highlightStepIndex = config.getHighlightStepIndex();
    if (highlightStepIndex !== undefined) {
      const methodWindow = shopWindowConfigs.atName(shopWindowNames.METHOD);
      if (methodWindow && methodWindow.hasSlides()) {
        const stepCount = experienceData.getClusterSize(
          methodWindow.clusterName,
          0,
        );
        if (highlightStepIndex <= stepCount) {
          const offset = methodWindow.hasOpeningSlide(experienceData) ? 1 : 0;
          defaultWindow = methodWindow;
          defaultWindow.index = highlightStepIndex - 1 + offset;
          if (
            experienceData.getClusterType(methodWindow.clusterName) ===
            clusterTypes.TIMELINE
          ) {
            videoTimelineName = methodWindow.clusterName;
          }
        }
      }
    }

    // Play-page deep-link: ?product=<id> opens the shop window on that product's
    // slide. Overrides the dsw default window when the product is found; silently
    // no-ops when the id is absent or not present in any shop window.
    const highlightProductId = config.getHighlightProductId();
    if (highlightProductId !== undefined) {
      const productSlide = resolveProductSlide(
        experienceData,
        shopWindowConfigs,
        highlightProductId,
      );
      if (productSlide) {
        defaultWindow = productSlide.config;
        defaultWindow.index = productSlide.index;
        if (
          experienceData.getClusterType(defaultWindow.clusterName) ===
          clusterTypes.TIMELINE
        ) {
          videoTimelineName = defaultWindow.clusterName;
        }
      }
    }

    if (!defaultWindow) {
      defaultWindow = firstWindow;
    }

    if (defaultWindow) {
      applicationData.setValue(
        valueNames.SHOP_WINDOW_DEFAULT_CONFIG,
        defaultWindow,
      );
      applicationData.setValue(valueNames.SHOP_WINDOW_CONFIG, defaultWindow);
    }

    if (videoTimelineName) {
      applicationData.setValue(
        valueNames.VIDEO_TIMELINE_NAME,
        videoTimelineName,
      );
    }
  }

  getShopWindowConfig(name: string): ShopWindowConfig | undefined {
    return this.shopWindowMap[name];
  }

  willSetValue(state: ExperienceState, key: string, value: unknown) {
    return false;
  }

  handleEvent(state: ExperienceState, e: PluginEvent) {
    const experienceData = state.experienceData;
    if (e.name === eventNames.CTA_DATA_DID_UPDATE) {
      if (
        e.cta &&
        experienceData.getIntegrationType() === integrationTypes.WHISK
      ) {
        // nothing for now.
      }
      return true;
    }
    return false;
  }
}
